import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { isEqual, values } from 'lodash';
import { sortOptions } from './utils';
import './styles.scss';

interface IInternalOptionType {
  label?: string;
  value?: string;
}

export interface IOwnProps {
  placeholder?: string;
  initialSelectKey?: string | boolean | null;
  options: IOptionType[];
  disabled?: boolean;
  disableSorting?: boolean;
  isSelectedOptionAtFirst?: boolean;
  emptyDataHelper?: string;
  onChange: (option: IOptionType) => void;
  selectedOption?: Nullable<IOptionType>;
}

export interface IState {
  isOpen: boolean;
  focusedOption: IInternalOptionType | IOptionType;
  selectedOption: IInternalOptionType | IOptionType;
  options: IOptionType[];
}

interface IOptionProps {
  option: IOptionType;
  isFocused?: boolean;
  disabled?: boolean;
  onClickOption?: () => void;
  onFocusOption?: () => void;
}

export const Option = (props: IOptionProps) => {
  const { option, isFocused, onClickOption, onFocusOption, disabled } = props;
  return (
    <div
      aria-label="option"
      data-testid={`option-${option.value}`}
      styleName={clsx(
        'select-option',
        isFocused && !disabled ? 'is-focused' : '',
        disabled ? 'is-disabled' : '',
      )}
      onClick={disabled ? undefined : onClickOption}
      onMouseEnter={disabled ? undefined : onFocusOption}
    >
      {option.renderLabel || option.label}
    </div>
  );
};

const defaultState = (props: IOwnProps): IState => {
  const selectedOption =
    props.options.find((option) => option.value === props.initialSelectKey) ||
    {};
  return {
    isOpen: false,
    focusedOption: selectedOption,
    selectedOption,
    options: props.options,
  };
};

const Select = (props: IOwnProps) => {
  const rootElement = useRef<Nullable<HTMLDivElement>>(null);
  const [selectState, setSelectState] = useState<IState>(defaultState(props));
  const [previousSelectedOption, setPreviousSelectedOption] = useState<
    Nullable<IInternalOptionType | IOptionType>
  >(selectState.selectedOption);
  const [forceScrollOffset, setForceScrollOffset] = useState<boolean>(false);

  const {
    placeholder = 'Select ...',
    emptyDataHelper = '',
    options,
    disabled,
    disableSorting = false,
    isSelectedOptionAtFirst = false,
  } = props;

  const toggle = () => {
    setSelectState((prevState) => ({
      ...prevState,
      isOpen: !prevState.isOpen,
    }));
  };

  const selectOption = (option: IOptionType) => {
    setSelectState((prevState) => ({
      ...prevState,
      focusedOption: option,
      selectedOption: option,
      isOpen: false,
    }));
    setForceScrollOffset(false);
  };

  const handleDocumentClick = useCallback(
    (e: MouseEvent) => {
      const target: any = e.target;
      if (
        selectState.isOpen &&
        rootElement &&
        rootElement.current &&
        !rootElement.current.contains(target)
      ) {
        setSelectState((prevState) => ({
          ...prevState,
          isOpen: false,
          focusedOption: prevState.selectedOption,
        }));
        setForceScrollOffset(false);
      }
    },
    [selectState.isOpen],
  );

  const scrollToOption = (
    menu: Nullable<HTMLDivElement>,
    selectedOption: IInternalOptionType | IOptionType,
  ) => {
    if (!menu) return;
    const selectedOptionElement = values(menu.children).find(
      (child) => child.textContent === selectedOption.label,
    ) as HTMLElement;

    if (selectedOptionElement && !forceScrollOffset) {
      menu.scrollTop =
        selectedOptionElement.offsetTop -
        3 * selectedOptionElement.clientHeight; // eslint-disable-line
      setForceScrollOffset(true);
    }
  };

  const scrollWindowIfOverflow = (menu: Nullable<HTMLDivElement>) => {
    if (!menu) return;
    const diff =
      menu.getBoundingClientRect().bottom - window.innerHeight - window.scrollY;
    if (diff > 0) {
      window.scrollTo(
        0,
        menu.getBoundingClientRect().bottom - window.innerHeight,
      );
    }
  };

  const onScrollMenu = (menu: Nullable<HTMLDivElement>) => {
    scrollToOption(menu, selectState.selectedOption);
    scrollWindowIfOverflow(menu);
  };

  const onClickOption = (option: IOptionType) => {
    selectOption(option);
    props.onChange(option);
  };

  const onFocusOption = (option: IOptionType) => {
    if (selectState.focusedOption.value !== option.value) {
      setSelectState((prevState) => ({
        ...prevState,
        focusedOption: option,
      }));
    }
  };

  let sortedOptions = disableSorting ? [...options] : sortOptions(options);
  if (isSelectedOptionAtFirst) {
    const optionIndex = sortedOptions.findIndex(
      (option) => option.value === selectState.selectedOption.value,
    );
    if (optionIndex !== -1) {
      const removedOption = sortedOptions.splice(optionIndex, 1);
      sortedOptions = [...removedOption, ...sortedOptions];
    }
  }

  useEffect(() => {
    if (
      props.selectedOption &&
      !isEqual(props.selectedOption, previousSelectedOption)
    ) {
      props.onChange(props.selectedOption);
      setSelectState((prevState) => ({
        ...prevState,
        focusedOption: props.selectedOption!,
        selectedOption: props.selectedOption!,
      }));
      setPreviousSelectedOption(props.selectedOption);
    }
  }, [previousSelectedOption, props]);

  useEffect(() => {
    if (props.options.length === 0 && !props.emptyDataHelper) {
      setSelectState(defaultState(props));
    }
  }, [props.options, props.emptyDataHelper, props]);

  useEffect(() => {
    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [handleDocumentClick]);

  return (
    <div
      ref={rootElement}
      styleName={clsx(
        'select-root',
        disabled ? 'select-root-disabled' : '',
      )}
      data-testid={`${
        sortedOptions.length > 1
          ? 'select-option-multiple'
          : 'select-option-single'
      }`}
    >
      <div
        data-testid="selection-menu"
        styleName={clsx(
          'select-control',
          selectState.isOpen ? 'is-open' : '',
        )}
        onClick={() => toggle()}
        role="button"
        tabIndex={0}
      >
        <div styleName={'select-placeholder'}>
          {selectState.selectedOption.label || placeholder}
        </div>
        <div styleName={`${selectState.isOpen ? 'down-arrow' : 'up-arrow'}`} />
      </div>
      {selectState.isOpen ? (
        <div
          data-testid="selection-options"
          styleName={'select-menu'}
          ref={onScrollMenu}
        >
          {sortedOptions.length === 0 ? (
            <Option
              key="empty-program"
              data-testid="selection-empty-option"
              option={{ value: 'empty', label: emptyDataHelper }}
              disabled
            />
          ) : (
            sortedOptions.map((option) => (
              <Option
                key={option.value}
                data-testid={'option-select'}
                option={option}
                isFocused={selectState.focusedOption.value === option.value}
                onFocusOption={() => onFocusOption(option)}
                onClickOption={() => onClickOption(option)}
                disabled={option.disabled}
              />
            ))
          )}
        </div>
      ) : null}
    </div>
  );
};

export default Select;
